/**
 * Print & Mail, as the API hands it over.
 *
 * Mirrors the backend's `IPrintMailConfig` (`modules/config/print-mail-config.service`), the
 * `MailPiece` / `MailBatch` entities and the DTOs under `api/print-mail`. Kept in one place so the
 * queue screen, the admin form and the "Mail this" action cannot drift from each other.
 */

/** Postage and envelope choices. Set once as tenant defaults; a purpose may override any of them. */
export interface IMailPostage {
  color: boolean
  doubleSided: boolean
  /** `usps_first_class` or `usps_standard`. Standard is cheaper, slower, and carries no extra service. */
  mailType: string
  /** `certified`, `certified_return_receipt`, `registered`, or '' for ordinary post. */
  extraService: string
  returnEnvelope: boolean
  perforatedPage: number
}

/** What a single caller may override for one piece, on top of the purpose's postage. */
export type IMailPostageOverrides = Partial<IMailPostage>

/** What a tenant pays per piece, so a run can be costed before anyone authorises it. */
export interface IMailRateCard {
  basePieceCents: number
  perPageCents: number
  colorPerPageCents: number
  certifiedCents: number
  certifiedReturnReceiptCents: number
  registeredCents: number
  returnEnvelopeCents: number
}

/** One communication type's postage, and whether bulk runs for it are switched on. */
export type IMailPurposeConfig = IMailPostageOverrides & { enabled?: boolean }

export interface IPrintMailConfig {
  enabled: boolean
  provider: string

  // The return address printed on every envelope. Blank falls back to the tenant's Footer block.
  fromName: string
  fromAddressLine1: string
  fromAddressLine2: string
  fromCity: string
  fromState: string
  fromZip: string

  postage: IMailPostage
  /** Keyed by the same string a piece carries as its `purpose`. */
  purposes: Record<string, IMailPurposeConfig>
  rateCard: IMailRateCard

  requireDeliverableAddress: boolean
  allowMissingUnit: boolean

  autoDispatch: boolean
  dispatchBatchSize: number
  /** 0 for no limit. Reaching it holds the rest rather than failing them. */
  dailyPieceCap: number
}

/**
 * Whether this tenant can post anything at all.
 *
 * `live` is the one that matters most on screen: a tenant posting real mail while believing they
 * are in a sandbox is the worst thing this feature can do, so it is never left to be inferred.
 */
export interface IPrintMailStatus {
  enabled: boolean
  provider: string
  /** A usable provider credential is saved. */
  configured: boolean
  /** That credential spends real postage, rather than being a sandbox key. */
  live: boolean
  /** What is still missing. Empty means ready. */
  missing: string[]
}

export interface IMailAddress {
  name?: string
  company?: string
  addressLine1: string
  addressLine2?: string
  city: string
  state: string
  zip: string
  country?: string
}

/** The platform's own vocabulary for where a piece has got to. Providers report more; these map. */
export const MAIL_PIECE_STATUS_LABEL: Record<string, string> = {
  queued: 'Queued',
  held: 'Held',
  submitted: 'Submitted',
  mailed: 'Mailed',
  in_transit: 'In transit',
  in_local_area: 'In local area',
  processed_for_delivery: 'Out for delivery',
  delivered: 'Delivered',
  returned_to_sender: 'Returned to sender',
  re_routed: 'Re-routed',
  canceled: 'Canceled',
  failed: 'Failed',
}

/** Nothing further is expected from these, so neither cancel nor release applies. */
export const MAIL_PIECE_TERMINAL_STATUSES = ['delivered', 'returned_to_sender', 'canceled', 'failed']

export const MAIL_PIECE_STATUS_STYLE: Record<string, string> = {
  queued: 'bg-slate-100 text-slate-700',
  held: 'bg-amber-50 text-amber-800',
  submitted: 'bg-sky-50 text-sky-800',
  mailed: 'bg-sky-50 text-sky-800',
  in_transit: 'bg-sky-50 text-sky-800',
  in_local_area: 'bg-sky-50 text-sky-800',
  processed_for_delivery: 'bg-sky-50 text-sky-800',
  delivered: 'bg-green-50 text-green-800',
  returned_to_sender: 'bg-red-50 text-red-800',
  re_routed: 'bg-amber-50 text-amber-800',
  canceled: 'bg-slate-100 text-slate-500',
  failed: 'bg-red-50 text-red-800',
}

export interface IMailTrackingEvent {
  event: string
  at: string
}

export interface IMailPiece {
  id: string
  loanNumber: number
  purpose: string
  batchId: string | null
  provider: string
  providerId: string | null
  status: string

  toName: string
  toCompany: string
  toAddressLine1: string
  toAddressLine2: string
  toCity: string
  toState: string
  toZip: string
  toCountry: string
  addressVerification: Record<string, any>

  documentKey: string
  documentName: string
  pageCount: number
  options: Record<string, any>
  /** From the tenant's rate card. An estimate: no provider returns a price on create. */
  estimatedCostCents: number

  trackingNumber: string
  carrier: string
  /** YYYY-MM-DD as the provider estimated it. Not a promise. */
  expectedDeliveryDate: string
  trackingEvents: IMailTrackingEvent[]

  attempts: number
  error: string | null
  createdBy: string
  submittedAt: string | null
  mailedAt: string | null
  deliveredAt: string | null
  canceledAt: string | null
  createdAt: string
  updatedAt: string
}

/** What the server filters on. A date range is NOT among them - see `QueueTab`. */
export interface IPrintMailPieceFilters {
  loanNumber?: number
  batchId?: string
  purpose?: string
  status?: string
  limit?: number
  offset?: number
}

export interface IMailBatch {
  id: string
  kind: string
  description: string
  status: 'running' | 'done' | 'error' | string
  /** Loans the run considered, written when it started. */
  total: number
  /** Pieces queued, written when it finished. */
  created: number
  skipped: Array<{ loanNumber: number; reason: string }>
  estimatedCostCents: number
  params: Record<string, any>
  error: string | null
  createdBy: string
  createdAt: string
  finishedAt: string | null
}

/**
 * One row of the mailing list.
 *
 * The first block is the generation candidate as the tax-form screen knows it, unchanged, so the
 * two screens cannot disagree about who qualifies; the rest is what only the post knows.
 */
export interface ITaxFormCandidate {
  loanNumber: number
  byteproFileName: string
  borrowerName: string
  /** Mortgage interest received in the tax year. `interest`, not `mortgageInterest`. */
  interest: number
  meetsThreshold: boolean
  hasEmail: boolean
  onHold: boolean
  alreadyGenerated: boolean
  /** Who the envelope goes to, which is not always the payer named on the form. */
  addressee: string
  cityStateZip: string
  /** Whether a form has actually been produced and stored, so there is something to post. */
  generated: boolean
  /** Why this loan is not getting a posted form. Empty when it is. */
  skipReason: string
}

/**
 * The mail run does not generate anything: it posts the forms the tax-form run already produced,
 * re-rendered from each stored row's frozen snapshot, so the posted copy is the copy that was
 * published and emailed. That is why there is no threshold or Box 6 option here - those belong to
 * generation, under Servicing > Tax Forms.
 */
export interface ITaxFormRunParams {
  year: number
  /** One generated batch. Omit to post every form stored for the year. */
  batchId?: number
  loanNumbers?: number[]
  previewOnly?: boolean
}

/** The purpose the year-end run stamps on every piece, and the key its postage is configured under. */
export const TAX_FORM_1098_PURPOSE = 'taxForm1098'

/**
 * Purposes the platform itself posts under, offered in the admin form and the "Mail this" action.
 *
 * `purpose` is free text on the API on purpose - a tenant may add their own - so this is a starter
 * list rather than a closed set. Anything already in the saved config is offered alongside these.
 */
export const KNOWN_MAIL_PURPOSES: Record<string, string> = {
  [TAX_FORM_1098_PURPOSE]: 'Form 1098 (year-end tax form)',
  statement: 'Billing statement',
  lateNotice: 'Late notice',
  balloonNotice: 'Balloon notice',
  insuranceNotice: 'Insurance notice',
  rateChangeNotice: 'Rate change notice',
  reinstatement: 'Reinstatement quote',
  payoff: 'Payoff statement',
  demandLetter: 'Demand letter',
  document: 'Other document',
}

export const mailPurposeLabel = (purpose: string) => KNOWN_MAIL_PURPOSES[purpose] || purpose

/** Cents to dollars, always with the cents showing. */
export const mailMoney = (cents?: number | null) =>
  cents == null
    ? ''
    : `$${(Number(cents) / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
